/**
 * @function lineToPoints
 * @description
 * Through the baseline's total length, it calculates the points in which to draw transects.
 * * **Remainder Logic:** - If a segment is shorter than the step distance, its length accumulates in the remainder
 *
 * **Process Flow:**
 * 1. Gets the baseline's total length
 * 2. Push the first point to the array to be returned
 * 3. Cover all path segments, calculating the distance and direction between consecutives points
 * 4. Adjustments using remainder system to ensure consistent spacing between transects]
 *
 *
 * @param {google.maps.Polyline} lineString - object representing the baseline
 * @param {number} step - distance between  in meters
 *
 * @example
 *  For a line with step = 100m:
 *  Segment 1: 80m → No transect, remainder = 80m
 *  Segment 2: 150m → Transect at 20m (100-80), remainder = 30m
 *  Segment 3: 120m → Transect at 70m (100-30), remainder = 50m
 *
 * @returns {Array<google.maps.LatLng>} Array of LatLng points where transects should be placed
 */
export function lineToPoints(lineString, step) {
  const path = lineString.getPath();
  const totalLength =
    window.google.maps.geometry?.spherical.computeLength(path);
  const points = [];
  let remainder = 0;
  points.push(path.getAt(0));

  for (let i = 1; i < path.getLength(); i++) {
    const segmentStart = path.getAt(i - 1);
    const segmentEnd = path.getAt(i);
    const segmentLength =
      window.google.maps.geometry?.spherical.computeDistanceBetween(
        segmentStart,
        segmentEnd
      );
    const heading = window.google.maps.geometry?.spherical.computeHeading(
      segmentStart,
      segmentEnd
    );
    const firstOffsetInSegment = step - remainder;
    const offsets = [];
    if (firstOffsetInSegment <= segmentLength) {
      for (
        let offset = firstOffsetInSegment;
        offset <= segmentLength;
        offset += step
      ) {
        offsets.push(offset);
      }
    }

    offsets.forEach((offset) => {
      const point = window.google.maps.geometry?.spherical.computeOffset(
        segmentStart,
        offset,
        heading
      );
      points.push(point);
    });

    offsets.length === 0
      ? (remainder += segmentLength)
      : (remainder = segmentLength - offsets[offsets.length - 1]);
    if (remainder >= step) remainder = remainder % step;
  }

  return points;
}

export function circleToPolyline(circle) {
  const circleCenter = circle.getCenter();
  const circleRadius = circle.getRadius();
  const path = [];

  for (let angle = 0; angle <= 360; angle += 10) {
    const point = window.google.maps.geometry?.spherical.computeOffset(
      circleCenter,
      circleRadius,
      angle
    );
    path.push(point);
  }

  return path;
}

export const downloadBaselineAsKML = (baseline, filename = "baseline.kml") => {
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
      <name>Baseline</name>
        <Placemark>
        <name>Baseline</name>
        <LineString> <coordinates>${baseline
          .map((side) => `${side[0]},${side[1]}`)
          .join(" ")}</coordinates> </LineString>
        </Placemark>
    </Document>
  </kml>`;

  triggerKMLDownload(kml, filename);
};

export const downloadTransectsAsKML = (
  transects,
  filename = "transects.kml"
) => {
  let kml = `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
      <Folder><name>Transectos</name>`;
  transects.forEach((transect) => {
    kml += `
        <Placemark>
        <name>Transecto ${transect.id}</name>
        <LineString> <coordinates>${transect.geometry.coordinates
          .map((side) => `${side[0]},${side[1]}`)
          .join(" ")}</coordinates> </LineString>
        </Placemark>`;
  });
  kml += `
      </Folder>
    </Document>
  </kml>`;

  triggerKMLDownload(kml, filename);
};

const triggerKMLDownload = (kml, filename) => {
  const blob = new Blob([kml], {
    type: "application/vnd.google-earth.kml+xml",
  });
  const link = document.createElement("a");
  link.setAttribute("href", URL.createObjectURL(blob));
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const lineStringCoordsKML = (coords) =>
  coords.map(([lng, lat]) => `${lng},${lat}`).join(" ");

const featureToPlacemarkKML = (feature, name) => {
  const geom = feature?.geometry;
  if (!geom) return "";
  if (geom.type === "LineString") {
    return `
        <Placemark>
        <name>${name}</name>
        <LineString> <coordinates>${lineStringCoordsKML(
          geom.coordinates
        )}</coordinates> </LineString>
        </Placemark>`;
  }
  if (geom.type === "MultiLineString") {
    const segments = geom.coordinates
      .map(
        (line) =>
          `<LineString> <coordinates>${lineStringCoordsKML(
            line
          )}</coordinates> </LineString>`
      )
      .join("");
    return `
        <Placemark>
        <name>${name}</name>
        <MultiGeometry>${segments}</MultiGeometry>
        </Placemark>`;
  }
  return "";
};

export const buildShorelinesKMLBody = (features, folderName = "Linhas de Costa") => {
  let body = `<Folder><name>${folderName}</name>`;
  features.forEach((f, i) => {
    const date = f.properties?.date ?? `Linha ${i + 1}`;
    body += featureToPlacemarkKML(f, String(date));
  });
  body += `</Folder>`;
  return body;
};

export const buildProjectionsKMLBody = (
  projections,
  folderName = "Linhas Futuras"
) => {
  let body = `<Folder><name>${folderName}</name>`;
  projections.forEach(({ horizon, points }) => {
    if (!points?.length) return;
    const coords = points
      .map((p) => `${p.lng},${p.lat}`)
      .join(" ");
    body += `
        <Placemark>
        <name>${horizon} anos</name>
        <LineString> <coordinates>${coords}</coordinates> </LineString>
        </Placemark>`;
  });
  body += `</Folder>`;
  return body;
};

export const buildBaselineKMLBody = (coords, folderName = "Linha de Base") => {
  if (!coords?.length) return "";
  return `<Folder><name>${folderName}</name>
        <Placemark>
        <name>${folderName}</name>
        <LineString> <coordinates>${lineStringCoordsKML(
          coords
        )}</coordinates> </LineString>
        </Placemark>
      </Folder>`;
};

export const buildTransectsKMLBody = (
  transects,
  folderName = "Transectos"
) => {
  let body = `<Folder><name>${folderName}</name>`;
  transects.forEach((transect) => {
    body += `
        <Placemark>
        <name>Transecto ${transect.id}</name>
        <LineString> <coordinates>${lineStringCoordsKML(
          transect.geometry.coordinates
        )}</coordinates> </LineString>
        </Placemark>`;
  });
  body += `</Folder>`;
  return body;
};

export const buildReferenceKMLBody = (
  feature,
  folderName = "Linha de Referência"
) => {
  if (!feature?.geometry) return "";
  return `<Folder><name>${folderName}</name>${featureToPlacemarkKML(
    feature,
    folderName
  )}</Folder>`;
};

export const downloadShorelineAsKML = (
  features,
  filename = "linhas-costa.kml"
) => {
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>${buildShorelinesKMLBody(features)}</Document>
  </kml>`;
  triggerKMLDownload(kml, filename);
};

export const downloadProjectionsAsKML = (
  projections,
  filename = "linhas-futuras.kml"
) => {
  const kml = `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>${buildProjectionsKMLBody(projections)}</Document>
  </kml>`;
  triggerKMLDownload(kml, filename);
};

export const buildShoreRKMLDocument = ({
  baseline,
  transects,
  shorelines,
  reference,
  projections,
  labels = {},
}) => {
  const parts = [];
  if (shorelines?.length)
    parts.push(buildShorelinesKMLBody(shorelines, labels.shorelines));
  if (reference)
    parts.push(buildReferenceKMLBody(reference, labels.reference));
  if (baseline?.length)
    parts.push(buildBaselineKMLBody(baseline, labels.baseline));
  if (projections?.length)
    parts.push(buildProjectionsKMLBody(projections, labels.future));
  if (transects?.length)
    parts.push(buildTransectsKMLBody(transects, labels.transects));

  return `<?xml version="1.0" encoding="UTF-8"?>
  <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
      ${parts.join("\n")}
    </Document>
  </kml>`;
};

export const downloadShoreRKML = (parts, filename = "shoreR-export.kml") => {
  triggerKMLDownload(buildShoreRKMLDocument(parts), filename);
};
